import React from 'react';
import { useEditorState } from '@tiptap/react';
import {
  Flex, IconButton, Tooltip, Divider, Select, Menu, MenuButton,
  MenuList, MenuItem, MenuDivider, SimpleGrid, Box, Button,
} from '@chakra-ui/react';
import {
  LuBold, LuItalic, LuUnderline, LuStrikethrough, LuCode, LuSubscript, LuSuperscript,
  LuPalette, LuHighlighter, LuAlignLeft, LuAlignCenter, LuAlignRight, LuAlignJustify,
  LuList, LuListOrdered, LuListChecks, LuQuote, LuSquareCode, LuMinus, LuLink,
  LuImage, LuYoutube, LuTable, LuFileCode, LuUndo2, LuRedo2, LuChevronDown, LuBan,
  LuFileText, LuFileStack,
} from 'react-icons/lu';

const TEXT_COLORS = [
  '#000000', '#4A5568', '#A0AEC0', '#E53E3E', '#DD6B20', '#F68633',
  '#D69E2E', '#38A169', '#319795', '#3182CE', '#5A67D8', '#805AD5',
  '#D53F8C', '#FFFFFF',
];

const HIGHLIGHT_COLORS = [
  '#FEFCBF', '#FED7D7', '#FEEBC8', '#C6F6D5', '#B2F5EA', '#BEE3F8',
  '#E9D8FD', '#FED7E2', '#EDF2F7', '#FBD38D',
];

const FONT_SIZES = [
  { label: 'Pequeno', value: '12px' },
  { label: 'Normal', value: null },
  { label: 'Grande', value: '18px' },
  { label: 'Enorme', value: '24px' },
];

const ToolButton = ({ label, icon, onClick, isActive, isDisabled }) => (
  <Tooltip label={label} fontSize="xs" openDelay={400}>
    <IconButton
      aria-label={label}
      icon={icon}
      size="sm"
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'orange' : 'gray'}
      onClick={onClick}
      isDisabled={isDisabled}
    />
  </Tooltip>
);

const ColorGrid = ({ colors, onPick, onClear, clearLabel }) => (
  <Box p={2}>
    <SimpleGrid columns={7} spacing={1} mb={2}>
      {colors.map((color) => (
        <Box
          key={color}
          as="button"
          type="button"
          w="22px"
          h="22px"
          borderRadius="sm"
          bg={color}
          border="1px solid"
          borderColor="gray.300"
          _hover={{ transform: 'scale(1.15)' }}
          onClick={() => onPick(color)}
        />
      ))}
    </SimpleGrid>
    <Button size="xs" variant="outline" leftIcon={<LuBan />} onClick={onClear} w="full">
      {clearLabel}
    </Button>
  </Box>
);

const EditorToolbar = ({ editor }) => {
  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (!ed) return null;
      return {
        canUndo: ed.can().undo(),
        canRedo: ed.can().redo(),
        headingValue: ed.isActive('heading', { level: 1 })
          ? '1'
          : ed.isActive('heading', { level: 2 })
          ? '2'
          : ed.isActive('heading', { level: 3 })
          ? '3'
          : 'p',
        fontSize: ed.getAttributes('textStyle').fontSize || null,
        bold: ed.isActive('bold'),
        italic: ed.isActive('italic'),
        underline: ed.isActive('underline'),
        strike: ed.isActive('strike'),
        code: ed.isActive('code'),
        subscript: ed.isActive('subscript'),
        superscript: ed.isActive('superscript'),
        highlight: ed.isActive('highlight'),
        alignLeft: ed.isActive({ textAlign: 'left' }),
        alignCenter: ed.isActive({ textAlign: 'center' }),
        alignRight: ed.isActive({ textAlign: 'right' }),
        alignJustify: ed.isActive({ textAlign: 'justify' }),
        bulletList: ed.isActive('bulletList'),
        orderedList: ed.isActive('orderedList'),
        taskList: ed.isActive('taskList'),
        blockquote: ed.isActive('blockquote'),
        codeBlock: ed.isActive('codeBlock'),
        link: ed.isActive('link'),
        inTable: ed.isActive('table'),
      };
    },
  });

  if (!editor || !state) return null;

  const setHeading = (value) => {
    const chain = editor.chain().focus();
    if (value === 'p') chain.setParagraph().run();
    else chain.setNode('heading', { level: Number(value) }).run();
  };

  return (
    <Flex wrap="wrap" gap={1} align="center" p={2} className="wiki-toolbar">
      <ToolButton label="Desfazer (Ctrl+Z)" icon={<LuUndo2 />} onClick={() => editor.chain().focus().undo().run()} isDisabled={!state.canUndo} />
      <ToolButton label="Refazer (Ctrl+Y)" icon={<LuRedo2 />} onClick={() => editor.chain().focus().redo().run()} isDisabled={!state.canRedo} />

      <Divider orientation="vertical" h="24px" mx={1} />

      <Select size="sm" w="120px" value={state.headingValue} onChange={(e) => setHeading(e.target.value)} borderRadius="md">
        <option value="p">Parágrafo</option>
        <option value="1">Título 1</option>
        <option value="2">Título 2</option>
        <option value="3">Título 3</option>
      </Select>

      <Menu>
        <MenuButton as={Button} size="sm" variant="ghost" rightIcon={<LuChevronDown />} fontWeight="normal">
          {FONT_SIZES.find((f) => f.value === state.fontSize)?.label || 'Tamanho'}
        </MenuButton>
        <MenuList minW="140px">
          {FONT_SIZES.map((f) => (
            <MenuItem
              key={f.label}
              fontSize={f.value || '14px'}
              onClick={() => {
                const chain = editor.chain().focus();
                if (f.value) chain.setFontSize(f.value).run();
                else chain.unsetFontSize().run();
              }}
            >
              {f.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <Divider orientation="vertical" h="24px" mx={1} />

      <ToolButton label="Negrito (Ctrl+B)" icon={<LuBold />} onClick={() => editor.chain().focus().toggleBold().run()} isActive={state.bold} />
      <ToolButton label="Itálico (Ctrl+I)" icon={<LuItalic />} onClick={() => editor.chain().focus().toggleItalic().run()} isActive={state.italic} />
      <ToolButton label="Sublinhado (Ctrl+U)" icon={<LuUnderline />} onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={state.underline} />
      <ToolButton label="Tachado" icon={<LuStrikethrough />} onClick={() => editor.chain().focus().toggleStrike().run()} isActive={state.strike} />
      <ToolButton label="Código inline" icon={<LuCode />} onClick={() => editor.chain().focus().toggleCode().run()} isActive={state.code} />
      <ToolButton label="Subscrito" icon={<LuSubscript />} onClick={() => editor.chain().focus().toggleSubscript().run()} isActive={state.subscript} />
      <ToolButton label="Sobrescrito" icon={<LuSuperscript />} onClick={() => editor.chain().focus().toggleSuperscript().run()} isActive={state.superscript} />

      <Menu closeOnSelect>
        <Tooltip label="Cor do texto" fontSize="xs" openDelay={400}>
          <MenuButton as={IconButton} size="sm" variant="ghost" aria-label="Cor do texto" icon={<LuPalette />} />
        </Tooltip>
        <MenuList>
          <ColorGrid
            colors={TEXT_COLORS}
            onPick={(color) => editor.chain().focus().setColor(color).run()}
            onClear={() => editor.chain().focus().unsetColor().run()}
            clearLabel="Cor padrão"
          />
        </MenuList>
      </Menu>

      <Menu closeOnSelect>
        <Tooltip label="Destacar texto" fontSize="xs" openDelay={400}>
          <MenuButton as={IconButton} size="sm" variant={state.highlight ? 'solid' : 'ghost'} colorScheme={state.highlight ? 'orange' : 'gray'} aria-label="Destacar" icon={<LuHighlighter />} />
        </Tooltip>
        <MenuList>
          <ColorGrid
            colors={HIGHLIGHT_COLORS}
            onPick={(color) => editor.chain().focus().setHighlight({ color }).run()}
            onClear={() => editor.chain().focus().unsetHighlight().run()}
            clearLabel="Remover destaque"
          />
        </MenuList>
      </Menu>

      <Divider orientation="vertical" h="24px" mx={1} />

      <ToolButton label="Alinhar à esquerda" icon={<LuAlignLeft />} onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={state.alignLeft} />
      <ToolButton label="Centralizar" icon={<LuAlignCenter />} onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={state.alignCenter} />
      <ToolButton label="Alinhar à direita" icon={<LuAlignRight />} onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={state.alignRight} />
      <ToolButton label="Justificar" icon={<LuAlignJustify />} onClick={() => editor.chain().focus().setTextAlign('justify').run()} isActive={state.alignJustify} />

      <Divider orientation="vertical" h="24px" mx={1} />

      <ToolButton label="Lista" icon={<LuList />} onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={state.bulletList} />
      <ToolButton label="Lista numerada" icon={<LuListOrdered />} onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={state.orderedList} />
      <ToolButton label="Checklist" icon={<LuListChecks />} onClick={() => editor.chain().focus().toggleTaskList().run()} isActive={state.taskList} />
      <ToolButton label="Citação" icon={<LuQuote />} onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={state.blockquote} />
      <ToolButton label="Bloco de código" icon={<LuSquareCode />} onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={state.codeBlock} />
      <ToolButton label="Separador" icon={<LuMinus />} onClick={() => editor.chain().focus().setHorizontalRule().run()} />
      <ToolButton
        label="Quebra de página (Ctrl+Enter) — começa uma página nova no documento"
        icon={<LuFileStack />}
        onClick={() => editor.chain().focus().insertPageBreak().run()}
      />

      <Divider orientation="vertical" h="24px" mx={1} />

      <ToolButton label="Link" icon={<LuLink />} onClick={() => editor.wikiRequestLink?.()} isActive={state.link} />
      <ToolButton label="Imagem" icon={<LuImage />} onClick={() => editor.wikiRequestImage?.()} />
      <ToolButton label="Vídeo do YouTube" icon={<LuYoutube />} onClick={() => editor.wikiRequestYoutube?.()} />
      <ToolButton label="Bloco HTML" icon={<LuFileCode />} onClick={() => editor.chain().focus().insertHtmlBlock().run()} />

      <Menu closeOnSelect>
        <Tooltip label="Tabela" fontSize="xs" openDelay={400}>
          <MenuButton as={IconButton} size="sm" variant={state.inTable ? 'solid' : 'ghost'} colorScheme={state.inTable ? 'orange' : 'gray'} aria-label="Tabela" icon={<LuTable />} />
        </Tooltip>
        <MenuList fontSize="sm">
          <MenuItem onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
            Inserir tabela 3x3
          </MenuItem>
          <MenuDivider />
          <MenuItem isDisabled={!state.inTable} onClick={() => editor.chain().focus().addRowBefore().run()}>Linha acima</MenuItem>
          <MenuItem isDisabled={!state.inTable} onClick={() => editor.chain().focus().addRowAfter().run()}>Linha abaixo</MenuItem>
          <MenuItem isDisabled={!state.inTable} onClick={() => editor.chain().focus().deleteRow().run()}>Excluir linha</MenuItem>
          <MenuDivider />
          <MenuItem isDisabled={!state.inTable} onClick={() => editor.chain().focus().addColumnBefore().run()}>Coluna à esquerda</MenuItem>
          <MenuItem isDisabled={!state.inTable} onClick={() => editor.chain().focus().addColumnAfter().run()}>Coluna à direita</MenuItem>
          <MenuItem isDisabled={!state.inTable} onClick={() => editor.chain().focus().deleteColumn().run()}>Excluir coluna</MenuItem>
          <MenuDivider />
          <MenuItem isDisabled={!state.inTable} onClick={() => editor.chain().focus().toggleHeaderRow().run()}>Alternar cabeçalho</MenuItem>
          <MenuItem isDisabled={!state.inTable} onClick={() => editor.chain().focus().mergeOrSplit().run()}>Mesclar / dividir células</MenuItem>
          <MenuItem isDisabled={!state.inTable} color="red.500" onClick={() => editor.chain().focus().deleteTable().run()}>Excluir tabela</MenuItem>
        </MenuList>
      </Menu>

      <Divider orientation="vertical" h="24px" mx={1} display={{ base: 'none', sm: 'block' }} />

      <Tooltip label="Importar / colar Markdown com visualização" fontSize="xs" openDelay={400}>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="orange"
          leftIcon={<LuFileText />}
          fontWeight="normal"
          onClick={() => editor.wikiRequestMarkdown?.()}
        >
          Markdown
        </Button>
      </Tooltip>
    </Flex>
  );
};

export default EditorToolbar;
